import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { RoleProvider } from "@/contexts/RoleContext";
import { AuthProvider } from "@/contexts/AuthContext";
import RequireRole from "@/components/auth/RequireRole";
import RequireAuth from "@/components/auth/RequireAuth";
import RequireAdminRole from "@/components/admin/RequireRole";
import AppShell from "@/components/layout/AppShell";

import Login from "@/pages/auth/Login";
import Signup from "@/pages/auth/Signup";
import AdminShell from "@/pages/admin/AdminShell";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminWorkspaces from "@/pages/admin/AdminWorkspaces";
import AdminWorkspaceDetail from "@/pages/admin/AdminWorkspaceDetail";
import AdminJobs from "@/pages/admin/AdminJobs";
import AdminPlans from "@/pages/admin/Plans";
import AdminFeatureFlags from "@/pages/admin/FeatureFlags";
import AdminModelProviders from "@/pages/admin/ModelProviders";
import AdminCredits from "@/pages/admin/Credits";
import AdminUsage from "@/pages/admin/Usage";
import AdminSystemEvents from "@/pages/admin/SystemEvents";
import AdminAuditLogs from "@/pages/admin/AuditLogs";
import AdminWebhooks from "@/pages/admin/Webhooks";
import AdminAutomations from "@/pages/admin/Automations";
import AdminSupportMode from "@/pages/admin/SupportMode";
import AdminEnterprise from "@/pages/admin/Enterprise";
import AdminUsers from "@/pages/admin/Users";
import AdminActivity from "@/pages/admin/Activity";
import AdminSettingsPage from "@/pages/admin/AdminSettings";
import AdminControl from "@/pages/admin/AdminControl";
import AdminPlaceholder from "@/pages/admin/PlaceholderPage";
import UnifiedInbox from "@/pages/admin/inbox/UnifiedInbox";
import WorkflowsList from "@/pages/admin/automation/WorkflowsList";
import WorkflowBuilder from "@/pages/admin/automation/WorkflowBuilder";
import ContactsPage from "@/pages/admin/contacts/ContactsPage";
import ContactDetailPage from "@/pages/admin/contacts/ContactDetailPage";
import WorkspaceContactsPage from "@/pages/admin/workspaces/WorkspaceContactsPage";
import AgencyContactsPage from "@/pages/admin/agencies/AgencyContactsPage";
import UserDetailPage from "@/pages/admin/users/UserDetailPage";

import Dashboard from "@/pages/Dashboard";
import CreateAI from "@/pages/CreateAI";
import Projects from "@/pages/Projects";
import Templates from "@/pages/Templates";
import BrandLab from "@/pages/BrandLab";
import BookLab from "@/pages/BookLab";
import EditorStudio from "@/pages/book-lab/EditorStudio";
import EditorWorkspace from "@/pages/book-lab/EditorWorkspace";
import BookLabHome from "@/pages/book-lab/home/BookLabHome";
import MarketingDesignEditor from "@/pages/marketing-design/MarketingDesignEditor";
import WorkbookLab from "@/pages/WorkbookLab";
import SlideLab from "@/pages/SlideLab";
import FlyerLab from "@/pages/FlyerLab";
import CoverLab from "@/pages/CoverLab";
import LaunchLab from "@/pages/LaunchLab";
import VoiceLab from "@/pages/VoiceLab";
import LogoLab from "@/pages/LogoLab";
import VideoLab from "@/pages/VideoLab";
import AIInfluencer from "@/pages/AIInfluencer";
import MarketingStudio from "@/pages/MarketingStudio";
import AgentAI from "@/pages/AgentAI";
import AutomationLab from "@/pages/AutomationLab";
import Contacts from "@/pages/Contacts";
import Email from "@/pages/Email";
import SMS from "@/pages/SMS";
import Analytics from "@/pages/Analytics";
import Integrations from "@/pages/Integrations";
import AdminLab from "@/pages/AdminLab";
import Settings from "@/pages/Settings";
import {
  PersonalProfile, Gifts, Subscription, Usage, PromoCode, Affiliate,
  ApiDashboardPage, Webhooks as SettingsWebhooks, ConnectedApps, Billing,
  PaymentMethods, WorkspaceProfile, Team, BrandDefaults, Storage,
  Privacy, Notifications, Support,
} from "@/pages/settings/sections";

import "@/App.css";

export default function App() {
  return (
    <ThemeProvider>
      <RoleProvider>
        <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public auth */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />

            {/* Admin portal — JWT super_admin gate (independent of AppShell) */}
            <Route path="/admin" element={<RequireAdminRole role="super_admin"><AdminShell /></RequireAdminRole>}>
              <Route index element={<AdminDashboard />} />

              {/* Primary nav */}
              <Route path="inbox" element={<UnifiedInbox />} />

              <Route path="opportunities" element={
                <AdminPlaceholder
                  testId="admin-opportunities"
                  title="Opportunities"
                  subtitle="Sales pipeline for enterprise leads, upgrades, and private-label deals"
                  willControl={[
                    "Enterprise leads captured from marketing site & in-app prompts.",
                    "Plan upgrade signals (workspaces hitting credit ceiling).",
                    "Usage-based upsell triggers (consistent high-throughput accounts).",
                    "Private-label / white-label deal pipeline with stage tracking.",
                    "Renewal forecasting and at-risk account alerts.",
                  ]}
                  relatedSources={[
                    { label: "opportunities",  value: "MongoDB · planned" },
                    { label: "workspaces",     value: "live · /api/admin/workspaces" },
                    { label: "credit_balances", value: "live · /api/admin/credits/global" },
                  ]}
                  status="Opportunity CRM planned for Turn E"
                  nextAction="Define opportunities schema (stage, owner, amount, source) and start emitting upsell signals from the usage worker."
                />}
              />

              <Route path="enterprise" element={<AdminEnterprise />} />

              <Route path="contacts" element={<ContactsPage />} />
              <Route path="contacts/:contactId" element={<ContactDetailPage />} />
              <Route path="workspaces/:workspaceId/contacts" element={<WorkspaceContactsPage />} />
              <Route path="agencies/:agencyId/contacts" element={<AgencyContactsPage />} />
              <Route path="users/:userId" element={<UserDetailPage />} />

              <Route path="users" element={<AdminUsers />} />
              <Route path="activity" element={<AdminActivity />} />
              <Route path="automation" element={<WorkflowsList />} />
              <Route path="automation/:workflowId" element={<WorkflowBuilder />} />
              <Route path="automations" element={<AdminAutomations />} />
              <Route path="settings" element={<AdminSettingsPage />} />

              {/* Internal Tools */}
              <Route path="control" element={<AdminControl />} />

              {/* Reachable from Admin Control (still routed individually) */}
              <Route path="workspaces" element={<AdminWorkspaces />} />
              <Route path="workspaces/:workspaceId" element={<AdminWorkspaceDetail />} />
              <Route path="jobs" element={<AdminJobs />} />
              <Route path="plans" element={<AdminPlans />} />
              <Route path="credits" element={<AdminCredits />} />
              <Route path="model-providers" element={<AdminModelProviders />} />
              <Route path="feature-flags" element={<AdminFeatureFlags />} />
              <Route path="usage" element={<AdminUsage />} />
              <Route path="system-events" element={<AdminSystemEvents />} />
              <Route path="audit-logs" element={<AdminAuditLogs />} />
              <Route path="webhooks" element={<AdminWebhooks />} />
              <Route path="support-mode" element={<AdminSupportMode />} />
            </Route>

            {/* Editor Studio — full-screen standalone layout (no AppShell, no Sidebar, no Topbar) */}
            <Route path="/book-lab/editor/home" element={<RequireAuth><BookLabHome /></RequireAuth>} />
            <Route path="/book-lab/editor" element={<RequireAuth><EditorWorkspace /></RequireAuth>} />
            <Route path="/marketing-design/editor" element={<RequireAuth><MarketingDesignEditor /></RequireAuth>} />

            <Route path="/" element={<AppShell />}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="create" element={<CreateAI />} />
              <Route path="projects" element={<Projects />} />
              <Route path="templates" element={<Templates />} />
              <Route path="brand-lab" element={<BrandLab />} />
              <Route path="book-lab" element={<Navigate to="/book-lab/editor/home" replace />} />
              <Route path="book-lab/editor-studio" element={<EditorStudio />} />
              <Route path="workbook-lab" element={<WorkbookLab />} />
              <Route path="slide-lab" element={<SlideLab />} />
              <Route path="flyer-lab" element={<FlyerLab />} />
              <Route path="cover-lab" element={<CoverLab />} />
              <Route path="launch-lab" element={<LaunchLab />} />
              <Route path="voice-lab" element={<VoiceLab />} />
              <Route path="logo-lab" element={<LogoLab />} />
              <Route path="video-lab" element={<VideoLab />} />
              <Route path="ai-influencer" element={<AIInfluencer />} />
              <Route path="marketing-studio" element={<MarketingStudio />} />
              <Route path="agent-ai" element={<AgentAI />} />
              <Route path="automation-lab" element={<AutomationLab />} />
              <Route path="contacts" element={<Contacts />} />
              <Route path="email" element={<Email />} />
              <Route path="sms" element={<SMS />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="integrations" element={<Integrations />} />

              {/* AdminLab is now role-gated; still exists at /admin and /adminlab */}
              <Route
                path="admin"
                element={<RequireRole><AdminLab /></RequireRole>}
              />
              <Route
                path="adminlab"
                element={<RequireRole><AdminLab /></RequireRole>}
              />

              {/* Settings with nested routes */}
              <Route path="settings" element={<Settings />}>
                <Route index element={<Navigate to="/settings/profile" replace />} />
                <Route path="profile" element={<PersonalProfile />} />
                <Route path="gifts" element={<Gifts />} />
                <Route path="subscription" element={<Subscription />} />
                <Route path="usage" element={<Usage />} />
                <Route path="promo-code" element={<PromoCode />} />
                <Route path="affiliate" element={<Affiliate />} />
                <Route path="api" element={<ApiDashboardPage />} />
                <Route path="webhooks" element={<SettingsWebhooks />} />
                <Route path="connected-apps" element={<ConnectedApps />} />
                <Route path="billing" element={<Billing />} />
                <Route path="payment-methods" element={<PaymentMethods />} />
                <Route path="workspace" element={<WorkspaceProfile />} />
                <Route path="team" element={<Team />} />
                <Route path="brand-defaults" element={<BrandDefaults />} />
                <Route path="storage" element={<Storage />} />
                <Route path="privacy" element={<Privacy />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="support" element={<Support />} />
              </Route>

              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        </AuthProvider>
      </RoleProvider>
    </ThemeProvider>
  );
}
